/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useMemo } from "react"
import { useSelector } from "react-redux"
import { TroyWebTracker } from "utils/tracking"
import { AppState } from "store/store.type"
import { EventAction, EventCategory, merchantType } from "constants/tracking"
import { BULK_UPLOAD_TYPE } from "../BulkUploadContext"

enum LOADING_STATUS {
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

enum IS_SUCCESS {
  SUCCESS = 1,
  FAILED = 0,
}

enum IS_ERROR {
  WITH_ERROR = 1,
  NO_ERROR = 0,
}

export const useScribeBulkUploadNotifications = (
  bulkUploadType: BULK_UPLOAD_TYPE,
) => {
  const {
    merchantGroupID,
    merchantID,
    outlet,
    outletV2,
    menuGroupDetail,
    enableTroyNewMerchantEndpoint,
  } = useSelector((state: AppState) => state.menu)

  return useMemo(() => {
    const categoryCompleted =
      bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
        ? EventCategory.inventoryEditCompleted
        : EventCategory.menuEditCompleted

    const categoryErrorFound =
      bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
        ? EventCategory.inventoryEditErrorfound
        : EventCategory.menuEditErrorfound

    const getMerchantType = () => {
      if (bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY) return undefined
      if (merchantGroupID) return merchantType.multi
      return merchantType.single
    }

    const getBusinessType = () => {
      if (menuGroupDetail?.menuGroupID) {
        return menuGroupDetail.businessType ?? 0
      }
      const currentOutlet = enableTroyNewMerchantEndpoint ? outletV2 : outlet
      return currentOutlet?.businessType ?? 0
    }

    const labels = {
      MERCHANT_GROUP_ID: merchantGroupID,
      STORE_ID: merchantID,
      MERCHANT_TYPE: getMerchantType(),
      BUSINESS_TYPE: getBusinessType(),
      MENU_GROUP_ID: menuGroupDetail?.menuGroupID,
    }

    return {
      trackSuccessfulShown: (taskID: string) => {
        TroyWebTracker.sendEvent({
          category: categoryCompleted,
          action: EventAction.editSuccessfulShown,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
            TASK_ID: taskID,
            LOADING_STATUS: LOADING_STATUS.SUCCESS,
            IS_SUCCESS: IS_SUCCESS.SUCCESS,
          },
        })
      },
      trackErrorFoundPageView: (taskID: string) => {
        TroyWebTracker.sendEvent({
          category: categoryErrorFound,
          action: EventAction.pageView,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
            TASK_ID: taskID,
            LOADING_STATUS: LOADING_STATUS.SUCCESS,
            IS_ERROR: IS_ERROR.WITH_ERROR,
          },
        })
      },
      trackCancelClick: (taskID: string) => {
        TroyWebTracker.sendEvent({
          category: categoryErrorFound,
          action: EventAction.clickCancel,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
            TASK_ID: taskID,
            LOADING_STATUS: LOADING_STATUS.SUCCESS,
            IS_ERROR: IS_ERROR.NO_ERROR,
          },
        })
      },
      trackDownloadCSVClick: (taskID: string) => {
        TroyWebTracker.sendEvent({
          category: categoryErrorFound,
          action: EventAction.clickDownloadcsv,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
            TASK_ID: taskID,
            LOADING_STATUS: LOADING_STATUS.SUCCESS,
            IS_ERROR: IS_ERROR.NO_ERROR,
          },
        })
      },
    }
  }, [
    merchantGroupID,
    merchantID,
    bulkUploadType,
    outlet,
    outletV2,
    menuGroupDetail,
    enableTroyNewMerchantEndpoint,
  ])
}
